import React, { Fragment, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  GetTicketContextDocument,
  IntegrationType,
  JiraConfigurationOptionsInput,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../../utils/integrationsUtils";
import { useJiraTicketContext } from "~/hooks/useJiraTicketContext";
import { JiraFormStepOne } from "./JiraFormStepOne";
import { JiraFormStepTwo } from "~/pages/integrations/hosted-integrations/forms/jira/JiraIntegrationForm/JiraFormStepTwo";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type JiraFormInput = {
  integrationName: string;
  host: string;
  email: string;
  apiToken: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
  defaultProject: string;
  cloudId: string;
};

const defaultValues: JiraFormInput = {
  integrationName: "",
  host: "",
  email: "",
  apiToken: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
  defaultProject: "",
  cloudId: "",
};

interface JiraIntegrationFormProps {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}

export function JiraIntegrationForm({
  scope,
  updateFlow,
}: JiraIntegrationFormProps) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  const stepFromParams = searchParams.get("step");
  const [step, setStep] = useState<number>(Number(stepFromParams) || 1);
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
    watch,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const { projects, isProjectsEmpty } = useJiraTicketContext({
    scopeMrn: updateFlow?.integration?.mrn || "",
    skip: !updateFlow?.integration.mrn,
  });

  const [watchAutoCreateOnDrift] = watch(["autoCreateTickets"]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      GetTicketContextDocument,
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "JiraConfigurationOptions") return;
      reset({
        host: configOptions.host,
        email: configOptions.email,
        integrationName: updateFlow.integration.name,
        autoCloseTickets: configOptions.autoCloseTickets,
        autoCreateTickets: configOptions.autoCreateCases,
        defaultProject: configOptions.defaultProject,
        cloudId: configOptions.cloudId || "",
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<JiraFormInput> = async (data) => {
    const jiraConfigurationOptions: JiraConfigurationOptionsInput = {
      host: data.host.trim(),
      email: data.email.trim(),
      apiToken: data.apiToken?.trim?.() || "",
      autoCloseTickets: data.autoCloseTickets,
      autoCreateCases: data.autoCreateTickets,
      defaultProject: data.defaultProject,
      cloudId: data.cloudId?.trim() || undefined,
    };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow || step === 2) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration?.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemJira,
              configurationOptions: {
                jiraConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        if (step === 1 && updateFlow) {
          setStep(2);
          return;
        } else {
          navigate(
            `/space/integrations/ticket_system_jira/${integrationId}/?${scope.params}`,
          );
        }
      } else {
        const { data: createIntegrationData } = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemJira,
              longLivedToken: false,
              configurationOptions: {
                jiraConfigurationOptions,
              },
            },
          },
        });
        const integrationId =
          createIntegrationData?.createClientIntegration.integration.mrn
            .split("/")
            .pop();
        if (step === 1) {
          navigate(
            `/space/integrations/ticket_system_jira/${integrationId}/edit?${scope.params}&step=2`,
          );
          return;
        }
      }
      if (redirectToParam) {
        navigate(redirectToParam);
      } else {
        navigate(`/space/integrations/ticket_system_jira?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["Atlassian Jira", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader {...{ type: IntegrationType.TicketSystemJira }} />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {step === 1 && (
            <JiraFormStepOne
              control={control}
              isUpdateFlow={!!updateFlow}
              errors={errors}
              getFieldState={getFieldState}
              isValid={isValid}
              disabled={!watchAutoCreateOnDrift}
              isProjectsEmpty={isProjectsEmpty}
              projects={projects}
            />
          )}

          {step === 2 && (
            <JiraFormStepTwo
              control={control}
              isValid={isValid}
              disabled={!watchAutoCreateOnDrift}
              isProjectsEmpty={isProjectsEmpty}
              projects={projects}
              isUpdateFlow={!!updateFlow}
            />
          )}
        </form>
      </Box>
    </Fragment>
  );
}
